// @ts-strict-ignore
/**
 * @module Webrtc
 */

import { Subject, Subscription, filter } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { Call, CallEvents, CallMedia, CallRB, CallStatus, CallType } from "rainbow-web-sdk/models/call.model";
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import type { VirtualBackgroundService } from 'rainbow-web-sdk/services/virtualBackground/virtualBackground.service';
import { VIRTUAL_BACKGROUND_SVC, VirtualBackgroundServiceEvents } from 'rainbow-web-sdk/services/virtualBackground/virtualBackground.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { WebrtcP2PEventHandler } from "rainbow-web-sdk/services/webrtc/webrtcP2PEventHandler";
import { WebrtcConfigAndUtils } from "rainbow-web-sdk/services/webrtc/webrtcConfigAndUtils";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { DeviceManagementEvents, DeviceManagementServiceRB } from 'rainbow-web-sdk/services/deviceManagement/deviceManagement.service';
import type { SpeechEnhancementService } from 'rainbow-web-sdk/services/speechEnhancement/speech-enhancement.service';
import { SPEECH_ENH_SVC } from 'rainbow-web-sdk/services/speechEnhancement/speech-enhancement.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { JingleSession, MediaStreamKind } from 'rainbow-web-sdk/libs/strophe/strophe.jingle.session';
import { ConversationServiceRB } from '../conversation/conversation.service';
import { ConversationRB } from 'rainbow-web-sdk/models/conversation.model';
import { allSettled } from '../../utils/allSettled';
import { stringifyError } from '../../utils/stringifyError';

/**
 * @internal
 */
export const WEBRTC_P2P_SVC = 'WebrtcP2PService';

/**
 * @eventProperty
 * WebrtcP2PService Events.
 */
export enum WebrtcP2PServiceEvents {
    /**
     * @eventProperty
     * This RB event is send when P2P called is busy (in order to manage a busy tone).
     */
    RAINBOW_ON_WEBRTC_BUSY = 'RAINBOW_ON_WEBRTC_BUSY',

    /**
     * @eventProperty
     * This RB event is send when we need to ask for snapshot from other ressources to update the list of calls
     */
    RAINBOW_SUPERVISION_SNAPSHOT_NEEDED = 'RAINBOW_SUPERVISION_SNAPSHOT_NEEDED',

    /**
     * @eventProperty
     * This RB event is send when a call is updated; This event can be used to manage all calls in single place;
     */
    RAINBOW_ON_CALL_UPDATED_EVENT = 'RAINBOW_ON_CALL_UPDATED_EVENT',

    /**
     * @eventProperty
     * This RB event is send when a MP call is updated
     */
    RAINBOW_ON_MEDIA_PILLAR_CALL_UPDATED_EVENT = 'RAINBOW_ON_MEDIA_PILLAR_CALL_UPDATED_EVENT'
}

/**
 * @public
 * Webrtc P2P Service interface
 */
export interface IWebrtcP2PService {

    /**
     * Make a standard P2P call toward a contact
     * @param contact - The Call object that we want to remove
     * @param mediaType - The media type of call (could be 'audio', 'video' (i.e audio and video), 'videoOnly' (no audio, only video), 'sharing' (audio and sharing) and 'sharingOnly' (only sharing, no audio))
     * @param message - [optional] additional message send to the distant party for the reason of the call
     * @returns Returns the call
     */
    makeCall(contact: Contact, mediaType: 'audio' | 'video' | 'videoOnly' | 'sharing' | 'sharingOnly', message?: string): Promise<Call | undefined>;

    /**
     * Answer an incoming webrtc call
     * @param call - The Call object that we want to remove
     * @param mediaType - (optional) The media type to answer the call (by default, it's only audio anwser). In case of audio and video incoming call
     *   the user can use type = "video" to answer the call with audio and video;
     */
    answerCall(call: CallRB, mediaType: string): void;

    /**
     *Terminate a webrtc call
     * @param call - The Call object that we want to remove
     * @param cause - (optional) cause to reject the call
     * @param notifyOtherParticipant -(optional) Indicates wether the other participant should be notified by a xmpp message or not
     */
    terminateCall(call: CallRB, cause: string, notifyOtherParticipant: boolean): void;

    /**
     * Allow to mute / unmute a call
     * @param call - The call
     * @param state - if true, the audio in the call will be muted
     * @returns The updated call object
     */
    muteCall(call: CallRB, state: boolean): CallRB;

    /**
     * Put the call on Hold
     * @param call - The call to be put on hold
     * @param hold - When true the call is hold
     * @returns the updated call object
     */
    holdCall(call: CallRB, state: boolean): CallRB;

    /**
     * Remove a media from call
     * @param call - The call object
     * @param mediaType - the mediaType to remove from the call
     * @returns Resolved when the action is taken into accout
     */
    removeMediaFromCall(call: CallRB, mediaType: 'video' | 'sharing'): Promise<CallRB>;

    /**
     *Allow to send DTMF
     * @param call - if true, the audio in the call will be muted
     * @param dialString - The conversation that is related to the call
     * @param skipHandle - [optional] Should skip the ontonechange event -- false by default
     */
    sendDTMF(call: CallRB, dialString: string, skipHandle?: boolean): Promise<void>;

    /**
     * Get a call by it's ID
     * @param callId - The id of the call to find
     * @returns The call
     */
    getCallById(callId: string): CallRB;

    /**
     * Find and return the current active call (the last one found), or null otherwise
     * @returns Return the current active call (the last one found), or null otherwise
     */
    getCurrentActiveCall(): CallRB | null;

    /**
     * If the current user has any kind of webrtc call (could also be with WebRTC GW / Media Pillar)
     * @returns True if user has a call
     */
    isUserContactInCall(): boolean;

    /**
     * Subscribe to updates on the current calls sessions (all events are of RBEvent type `{ name: string, data:any }`);
     * All public events that are fired by this service are described in the documentation.
     * NB There might be some events that are not documented here, they should be ignored
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventName - The name of the event to filter
     * @returns Returns RxJS Subscription
     */
    subscribe(handler: any, eventName?: string): Subscription;
}

/**
 * @internal
 */
export class WebrtcP2PService extends Service implements IWebrtcP2PService {

    public started = false;
    public connected = false;
    public RTC = null;
    public calls: Record<string, CallRB> = {};
    public autoreleaseTimeout = null;
    public makingCall = false;
    public reconnectCall = false;
    public alreadyAttaching = false;
    public firefoxPreferedSharing = "window";
    public webrtcP2PEventHandler: WebrtcP2PEventHandler = null;

    private isMixPstnWebRtcAllowed: boolean = false;
    private audioProfileChanging = false;
    private subscriptions: Subscription[] = [];
    private xmppConnectionSubscription: Subscription = null;
    private replaceBackgroundSubscription: Subscription = null;
    private deviceManagementSubscription: Subscription = null;
    private listeners = [];

    // Services
    public virtualBackgroundService?: VirtualBackgroundService;
    public speechEnhancementService?: SpeechEnhancementService;
    private logger: LoggerService = LoggerService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private rxSubject: Subject<any> = new Subject<any>();
    private eventService: EventService = EventService.getInstance();
    private webrtcConfigAndUtils: WebrtcConfigAndUtils = WebrtcConfigAndUtils.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private deviceManagementService: DeviceManagementServiceRB = DeviceManagementServiceRB.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();

    // Functions that are overwritten by CentralizedService
    public getDesktopSharingSource(): any { return null; }
    public askToAddSharing(__conversationId, __shouldAskForAudio = false) { return false; }
    public askToStartSharing(__contactId, __media, __shouldAskForAudio = false) { return false; }
    public isNativeApplication() { return false; }

    // ************************************************************
    // ** SERVICE LIFECYCLE                                      **
    // ************************************************************

    public static getInstance() { return Service.create(WEBRTC_P2P_SVC, WebrtcP2PService); }
    public static build() { return new WebrtcP2PService(); }
    private constructor() { super(); }

    public start(): void {

        // Set RTC configuration
        this.RTC = this.webrtcConfigAndUtils.getBrowserMethodHandler();

        this.started = true;
        this.connected = true;

        this.subscriptions.push(this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onConnectionStateChangeEvent(event); }));
        this.subscriptions.push(this.webrtcConfigAndUtils.subscribe((event: RBEvent) => { this.webrtcConfigAndUtilsEvent(event); }));

        this.listeners.push(this.eventService.subscribe("PUT_ACTIVE_WEBRTC_CALL_OR_CONF_ON_HOLD", () => { this.putActiveWebrtcCallOnHold(); }));
        this.listeners.push(this.eventService.subscribe("ON_AUDIO_STREAM_TRACK_ENDED", () => {
            if (this.getCurrentActiveCall()) {
                setTimeout(() => {
                    this.updateLocalAudioStreamForCall(this.getCurrentActiveCall());
                }, 3000);
            }
        }));

        this.attachHandlers();

        this.virtualBackgroundService = this.mainService.inject<VirtualBackgroundService>(VIRTUAL_BACKGROUND_SVC);
        this.speechEnhancementService = this.mainService.inject<SpeechEnhancementService>(SPEECH_ENH_SVC);

        if (this.virtualBackgroundService) {
            this.subscriptions.push(this.virtualBackgroundService.subscribe(
                (event) => { this.onReplaceBackgroundChangeEvent(event); },
                VirtualBackgroundServiceEvents.UPDATE_STREAM
            ));
        }

        this.subscriptions.push(this.deviceManagementService.subscribe((event: RBEvent) => {
            try {
                if (event.name === DeviceManagementEvents.ON_NEW_AUDIO_VIDEO_DEVICE_SELECTED) {
                    this.logger.info(`[webrtcP2PService] deviceManagement event -- ${event.name} -- ${event.data.kind}`);
                    if (event.data.kind === "audioinput") {
                        //change microphone for current active calls;
                        setTimeout(() => {
                            this.onAudioVideoInputDeviceChange();
                        }, 1000);
                    }
                    else if (event.data.kind === "videoinput") {
                        //change microphone for current active calls;
                        setTimeout(() => {
                            this.onAudioVideoInputDeviceChange();
                        }, 1000);
                    }
                }
            }
            catch {
                //do nothing
            }
        }));

        this.isMixPstnWebRtcAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS_FOR_PBX) || config.mixwebrtcpstn || config.mixwebrtcpstnhybride;

        this.logger.info("[webrtcP2PService] SERVICE | === STARTED ===");
    }

    public stop(): void {
        try {
            if (this.started) {
                this.started = false;
                this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); });
                this.subscriptions = [];

                let listener;
                while ((listener = this.listeners.pop())) {
                    listener();
                }

                Object.keys(this.calls).forEach((key) => {
                    if (this.calls[key]) {
                        if (this.calls[key].statsInterval) {
                            window.clearInterval(this.calls[key].statsInterval);
                            this.calls[key].statsInterval = null;
                        }
                        this.terminateCall(this.calls[key]);
                    }
                });

                // Terminate the session
                this.xmppService.connection.jingle.terminateAllSessions();

                this.connected = false;
                this.reconnectCall = false;
                this.makingCall = false;

                this.webrtcConfigAndUtils.stop();

                this.removeHandlers();

                this.logger.info("[webrtcP2PService] === STOPPED ===");
            }
        }
        catch (error) {
            this.logger.info("[webrtcP2PService] SERVICE | === STOPPING ERROR : " + error);
        }
    }

    public reconnect() {
        this.reconnectSessionsIfNeeded();
    }

    private attachHandlers() {
        this.logger.info("[webrtcP2PService] attachHandlers");

        this.removeHandlers();
        this.webrtcP2PEventHandler = new WebrtcP2PEventHandler(this);
        this.reconnectSessionsIfNeeded();
    }

    private removeHandlers() {
        this.logger.info("[webrtcP2PService] removeHandlers");

        if (this.webrtcP2PEventHandler) {
            this.webrtcP2PEventHandler.destroy();
            this.webrtcP2PEventHandler = null;
        }
    }

    private reconnectSessionsIfNeeded() {
        this.logger.info("[webrtcP2PService] | reconnectSessionsIfNeeded");
        this.connected = true;

        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.statsInterval) {
                    window.clearInterval(call.statsInterval);
                    call.statsInterval = null;
                }

                const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

                if (this.reconnectCall && session && session.peerconnection) {
                    this.logger.info("[webrtcP2PService] | onConnectionStateChangeEvent : connected - start reconnection on session " + call.id);
                    // session.connection = this.xmppService.connection.jingle.connection;
                    session.me = this.contactService.userContact.fullJid;
                    if (session.isInitiator) {
                        session.initiator = session.me;
                    }

                    session.connection = this.xmppService.connection;

                    this.contactService.setBusyState("dnd", this.calculatePresenceMessage(call));
                    setTimeout(() => {
                        if (session.peerconnection) {
                            const state = session.peerconnection.connectionState ? session.peerconnection.connectionState : session.peerconnection.iceConnectionState;
                            if (session.peerconnection.signalingState === "stable" && (state !== "failed" && state !== "disconnected")) {
                                this.logger.info("[webrtcP2PService] sendTransportReplaceForSession not needed for " + session.sid);
                                return;
                            }
                            session.reconnectSession();
                        }

                    }, 2000);

                }
                else if (!session) {
                    //missing session, reject/retract call
                    this.terminateCall(call);
                }
            }
        });
        this.reconnectCall = false;
    }

    private webrtcConfigAndUtilsEvent(event: RBEvent) {
        try {
            if (event.name === 'ON_CHROME_STOP_SCREEN_SHARING_TRIGGERED') {
                const currentCall = this.getActiveWebrtcAudioCall();
                if (currentCall && currentCall.localMedia & CallMedia.SHARING) {
                    this.removeSharingFromCall(currentCall);
                }
            }
        }
        catch (error) {
            this.logger.debug(`[webrtcP2PService] webrtcConfigAndUtilsEvent error -- ${event.name} -- ${stringifyError(error)}`);
        }
    }

    /**
     * Send an event to the RX Subject (sends always a RBEvent type)
     * @param name - The name of the event
     * @param data - The data of the event
     */
    public sendEvent(name: string, data: any = null): void {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    // ************************************************************
    // ** PUBLIC API                                             **
    // ************************************************************

    /**
     * Subscribe to updates on the current calls sessions (all events are of RBEvent type {name: string, data:any});
     * All public events that are fired by this service are described in the documentation.
     * NB There might be some events that are not documented here, they should be ignored
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventName - The name of the event to filter
     * @returns Returns RxJS Subscription
     */
    public subscribe(handler: any, eventName?: string): Subscription {
        return this.rxSubject.pipe(filter((event: RBEvent) => { return !eventName || event.name === eventName; })).subscribe(handler);
    }

    /**
     * Make a standard P2P call toward a contact
     * @param contact - The Call object that we want to remove
     * @param mediaType - The media type of call (could be 'audio', 'video' (i.e audio and video), 'videoOnly' (no audio, only video), 'sharing' (audio and sharing) and 'sharingOnly' (only sharing, no audio))
     * @param message - [optional] additional message send to the distant party for the reason of the call
     * @param message - [optional] TRUE in case we want to propose add System audio on top of sharing
     * @returns Returns the call
     */
    public async makeCall(contact: Contact, mediaType: string, message: string = '', addAudioToSharingCall = false): Promise<CallRB> {
        this.logger.info(`[webrtcP2PService] MEDIA   | makeCall to ${JSON.stringify({ displayName: contact.nameForLogs, resources: contact.resources })}`);

        if (this.makingCall && mediaType !== "sharingOnly") { this.logger.info("[webrtcP2PService] | makeCall already making a call !"); return; }
        this.makingCall = true;

        if (mediaType !== "sharingOnly") {

            // Put on hold any active PSTN call in Computer Mode
            if (this.isMixPstnWebRtcAllowed && this.existMediaPillarAudioCall()) this.eventService.publish("PUT_ACTIVE_TELEPHONY_CALL_ON_HOLD");

            // Fetch for an eventual active webrtc call
            const activeCall = this.getActiveWebrtcAudioCall();
            if (activeCall) {
                this.putCallOnHold(activeCall);
                this.eventService.publish("CREATE_RAINBOW_ANALYTICS_EVENT", { category: "multi-call-management", action: "make-call-without-prior-hold" });
            }
            else if (this.getCallOnHold()) {
                // We've a call already onhold
                this.eventService.publish("CREATE_RAINBOW_ANALYTICS_EVENT", { category: "multi-call-management", action: "make-call-after-hold" });
            }
        }

        // Activate blue border highlighting for sharing call
        if (mediaType === 'sharing' || mediaType === 'sharingOnly') {
            this.sendEvent("startHighlighting");

            //in case of sharing, we need to propose to get the audio
            //but there's difference between web and desktop
            if (!this.mainService.isDesktopApp()) addAudioToSharingCall = true;
        }

        return await this.webrtcP2PEventHandler.sendProposition(contact, mediaType, message, addAudioToSharingCall);
    }

    /**
     * Answer an incoming webrtc call
     * @param call - The Call object that we want to remove
     * @param mediaType - (optional) The media type to answer the call (by default, it's only audio anwser). In case of audio and video incoming call
     *   the user can use type = "video" to answer the call with audio and video;
     */
    public answerCall(call: CallRB, mediaType: string = ''): void {
        // Do not put on hold pstn call if this is webrtc sharing only call on top of PBX
        // TO DO: How to know this is the case ?? Add property in call ?
        if (!call.isMediaPillarCall() && call.remoteMedia !== CallMedia.SHARING) this.eventService.publish("PUT_ACTIVE_TELEPHONY_CALL_ON_HOLD");

        const activeCall = this.getActiveWebrtcAudioCall();
        if (activeCall) {
            // Send event for the analytics
            this.putCallOnHold(activeCall);
            this.eventService.publish("CREATE_RAINBOW_ANALYTICS_EVENT", { category: "multi-call-management", action: "take-call-without-prior-hold" });
        }
        else if (this.getCallOnHold()) {
            // We've a call already onhold
            this.eventService.publish("CREATE_RAINBOW_ANALYTICS_EVENT", { category: "multi-call-management", action: "take-call-after-hold" });
        }

        this.webrtcP2PEventHandler.acceptProposition(call, mediaType);
    }

    /**
     * Terminate a webrtc call
     * @param call - The Call object that we want to remove
     * @param cause - (optional) cause to reject the call
     * @param notifyOtherParticipant - (optional) Indicates wether the other participant should be notified by a xmpp message or not
     */
    public terminateCall(call: CallRB, cause: string = '', notifyOtherParticipant: boolean = true): void {
        if (!call) { this.logger.info("[webrtcP2PService] MEDIA   | terminateCall - trying to terminateCall a non existing call !"); return; }
        this.makingCall = false;

        if (this.autoreleaseTimeout) clearTimeout(this.autoreleaseTimeout);

        if (this.xmppService.connection.jingle.sessions[call.id]) this.releaseCall(call);
        else {
            if (call.isInitiator) this.webrtcP2PEventHandler.retractProposition(call);
            else this.webrtcP2PEventHandler.rejectProposition(call, cause, notifyOtherParticipant);
        }
    }

    /**
     * Allow to mute / unmute acall
     * @param call - The call
     * @param state - if true, the audio in the call will be muted
     * @returns The updated call object
     */
    public muteCall(call: CallRB, state: boolean): CallRB {
        this.logger.info("[webrtcP2PService] MEDIA   | muteCall: " + call + " | state: " + state);

        call.isMuted = state;

        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        if (session) session.muteAudio(state);
        else throw (new Error("[webrtcP2PService] MEDIA   | muteCall - trying to mute a non existing session !"));

        const conversation = this.conversationService.getConversationFromCache(call.contact.id);
        this.eventService.publish("ON_CONVERSATION_CALL_MUTED_UPDATED_EVENT", { conversation: conversation, call });

        this.conversationService.updateCallCapabilities(call);

        return call;
    }

    /**
     * Put the call on Hold
     * @param call - The call to be put on hold
     * @param  hold - When true the call is hold
     * @returns the updated call object
     */
    public holdCall(call: CallRB, hold: boolean): CallRB {
        if (hold) this.putCallOnHold(call); else this.retrieveCallOnHold(call);
        return call;
    }

    /**
     * Remove a media from call
     * @param call - The call object
     * @param mediaType - the mediaType to remove from the call
     */
    public async removeMediaFromCall(call: CallRB, mediaType: 'video' | 'sharing'): Promise<CallRB> {
        switch (mediaType) {
            case 'video': return this.removeVideoFromCall(call); break;
            case 'sharing': return this.removeSharingFromCall(call); break;
            default: return call;
        }
    }

    /**
     * Allow to send DTMF
     * @param call - if true, the audio in the call will be muted
     * @param dialString - The conversation that is related to the call
     * @param skipHandle - [optional] Should skip the ontonechange event -- false by default
     */
    public async sendDTMF(call: CallRB, dialString: string, skipHandle: boolean = false) {
        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        if (session?.peerconnection) {
            const senders = await session.getSenders();
            //find audio sender with dtmf sending capability
            const audioDtmfSender = senders.find(function (sender) {
                return sender.dtmf && sender.track && sender.track.enabled && sender.track.kind === "audio";
            });
            const dtmfSender = audioDtmfSender ? audioDtmfSender.dtmf : null;
            if (dtmfSender) {
                this.logger.info("[webrtcP2PService] sendDTMF: dtmf sender found on webrtc session for call id " + call.id + " dialstring: " + dialString);

                // Set tone event callback
                if (!skipHandle) { dtmfSender.ontonechange = this.handleToneChangeEvent.bind(this); }
                else { dtmfSender.ontonechange = this.silentToneChangeEventHandle.bind(this); }

                // Insert dtmf payload packet in audio rtp stream
                try { dtmfSender.insertDTMF(dialString); }
                catch (error) { this.logger.error("[webrtcP2PService]sendDTMF: invalid : " + error); }
            }
            else this.logger.error("[webrtcP2PService]sendDTMF: no dtmf sender found on webrtc session for call id " + call.id);
        }
        else this.logger.error("[webrtcP2PService]sendDTMF: no webrtc session found for call id " + call.id);
    }

    // ************************************************************
    // ** UCAAS PUBLIC API                                       **
    // ************************************************************

    /**
     * Remove The Call object and terminates the webrtc session related to this call.
     * @param call - The Call object that we want to remove
     * @param reason - The reason to end the call that is send to the distant
     * @param text - Additional text send to the distant when the webrtc session is released
     */
    public removeCallObject(call: CallRB, reason: string = "", text: string = "") {
        try {
            if (call) {
                this.logger.info("[webrtcP2PService] removeCallObject with id: " + call.id);

                // Update call object
                call.setStatus(CallStatus.ENDED);
                call.stopDuration();
                const session: JingleSession = this.xmppService.connection.jingle.sessions ? this.xmppService.connection.jingle.sessions[call.id] : null;

                if (this.speechEnhancementService) this.speechEnhancementService.stopProcessStream(call.id, "[webrtcP2PService]");

                if (session) {
                    this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
                }

                this.webrtcConfigAndUtils.disableAllStreamsForSession(call);

                if (call.originalStream) {
                    call.originalStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                    call.originalStream = null;
                    this.virtualBackgroundService?.stopProcessStream();
                }

                if (call.statsInterval) {
                    window.clearInterval(call.statsInterval);
                    call.statsInterval = null;
                }

                if (this.webrtcConfigAndUtils.callsStats[call.id]) {
                    this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);
                    delete this.webrtcConfigAndUtils.callsStats[call.id];
                }

                // Remove this call from call stack
                delete this.calls[call.id];

                this.sendUpdateCallEvent(call);

                if (session) {
                    this.xmppService.connection.jingle.terminate(call.id, reason, text);
                }

                this.sendEvent("stopHighlighting");

                this.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);
            }
        } catch (error) {
            this.logger.info("[webrtcP2PService] removeCallObject error " + error);
        }
    }

    // Manage background change event
    private onReplaceBackgroundChangeEvent(event) {
        try {
            const call = this.getCurrentActiveCall();
            if (call) {
                this.logger.info(`[webrtcP2PService] onReplaceBackgroundChangeEvent`);
                this.changeBackgroundForCall(call, event.data, event.data.type === "none");
            }
        }
        catch (_error) { /**/ }
    }

    // Event to put webrtc call on hold
    private putActiveWebrtcCallOnHold() {
        const call = this.getActiveWebrtcAudioCall();
        if (call) {
            this.putCallOnHold(call);
        }
    }

    // Manage XMPP connection changes
    private onConnectionStateChangeEvent(event: RBEvent) {
        try {
            if (event.name === 'ON_XMPP_DISCONNECTED') {
                this.logger.info("[webrtcP2PService] | onConnectionStateChangeEvent : disconnected");
                this.connected = false;
            }
            else if (event.name === 'ON_XMPP_CONNECTED') {
                //do nothing
                this.connected = true;
                this.attachHandlers();
            }
        } catch (error) {
            this.logger.info("[webrtcP2PService] | onConnectionStateChangeEvent : disconnected error : " + error);
        }
    }

    public isUserContactInCall(): boolean {
        let result = false;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && call.callStatus !== CallStatus.RINGING_INCOMMING) {
                    result = true;
                }
            }
        });
        return result;
    }

    public isUserContactInOtherCall(call: CallRB): boolean {
        let result = false;
        if (!call) return false;

        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const callwebrtc: CallRB = this.calls[key];

                if (callwebrtc.callStatus !== CallStatus.ENDED && callwebrtc.id !== call.id) {
                    result = true;
                }
            }
        });
        return result;
    }

    /**
     * Find and return the current active call (the last one found), or null otherwise
     * @returns Return the current active call (the last one found), or null otherwise
     */
    public getCurrentActiveCall(): CallRB | null {
        let result: CallRB | null = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.callStatus !== CallStatus.ENDED) result = call;
            }
        });
        return result;
    }

    /**
     * Retrieve the list of calls with a status equal to a given status (or a list of given status) (if some exist)
     * @public
     * @param {any} [searchedCallStatus] Status (or array of status) of calls to search. If not passed, it returns all calls except incoming/queued ringing calls
     * @returns {CallRB[]} List of calls matching the searched status (or empty array if no calls found)
     * @instance
     * @function
     */
    public getCalls(searchedCallStatus: CallStatus[] | CallStatus = null): CallRB[] {
        const calls: CallRB[] = [];
        const searchedStatusArray = searchedCallStatus
            ? (Array.isArray(searchedCallStatus) ? searchedCallStatus : [searchedCallStatus])
            : [
                //default returned calls: all calls except incoming calls
                CallStatus.DIALING,
                CallStatus.RINGING_OUTGOING,
                CallStatus.QUEUED_OUTGOING,
                CallStatus.ACTIVE,
                CallStatus.HOLD,
                CallStatus.PUT_ON_HOLD,
                CallStatus.ERROR
            ];

        Object.keys(this.calls || {}).forEach((key) => {
            const call = this.calls[key];
            const status = call.callStatus;

            if (searchedStatusArray.includes(status)) {
                calls.push(call);
            }
        });
        return calls;
    }

    /**
     * Return an array with all the WebRTC Jingle active sessions
     * @returns Return an array with all the WebRTC Jingle active sessions
     */
    public getCurrentActiveSessions(): any[] {
        const activeSessions = [];
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.callStatus !== CallStatus.ENDED) {
                    const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
                    if (session) activeSessions.push(session);
                }
            }
        });
        return activeSessions;
    }

    /**
     * Return an array with all the Call objects that are not ended and not related to MP
     * @returns Return an array with all the Call objects that are not ended and not related to MP
     */
    public getCurrentCallsNotMediaPillar(): CallRB[] {
        const activeCalls: CallRB[] = [];
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && !call.isMediaPillarCall() && !call.isSecondSharingCall) {
                    activeCalls.push(call);
                }
            }
        });
        return activeCalls;
    }

    /**
     * Return an array with all the Call objects that are not ended and not related to MP
     * @returns Return an array with all the Call objects that are not ended and not related to MP
     */
    public getCurrentIncomingCallsNotMediaPillar(): CallRB[] {
        const activeCalls: CallRB[] = [];
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus === CallStatus.RINGING_INCOMMING && !call.isMediaPillarCall() && !call.isSecondSharingCall) {
                    activeCalls.push(call);
                }
            }
        });
        return activeCalls;
    }

    /**
     * Check if the user has an active webrtc session that is not related to Media Pillar / WebRTC GW
     * @returns Return true if the user has an active webrtc session that is not related to Media Pillar / WebRTC GW
     */
    public existWebrtcActiveSessionNotMP(): boolean {
        let result = false;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && this.xmppService.connection.jingle.sessions[call.id] && !call.isMediaPillarCall() && !call.isSecondSharingCall) {
                    result = true;
                }
            }
        });
        return result;
    }

    /**
     * Check if the user has an active webrtc session that is related to Media Pillar / WebRTC GW
     * @returns Return true if the user has an active webrtc session that is related to Media Pillar / WebRTC GW
     */
    public existMediaPillarAudioCall(): boolean {
        let result = false;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.callStatus !== CallStatus.ENDED && (call.localMedia & CallMedia.AUDIO) && call.isMediaPillarCall()) {
                    result = true;
                }
            }
        });
        return result;
    }

    /**
     * Check if the user has an answering webrtc session that is related to Media Pillar / WebRTC GW
     * @returns Return true if the user has an answering webrtc session that is related to Media Pillar / WebRTC GW
     */
    public existMediaPillarAnsweringCall(): boolean {
        let result = false;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.callStatus === CallStatus.ANSWERING && call.isMediaPillarCall()) {
                    result = true;
                }
            }
        });
        return result;
    }

    /**
     * Return an array of Call that are related to Media Pillar / Webrtc GW
     * @returns Return an array of Call that are related to Media Pillar / Webrtc GW
     */
    public getMediaPillarAudioCalls(): CallRB[] {
        const mpAudioCalls: CallRB[] = [];
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && (call.localMedia & CallMedia.AUDIO) && call.isMediaPillarCall()) {
                    mpAudioCalls.push(call);
                }
            }
        });
        return mpAudioCalls;
    }

    /**
     * Return an array of Call that are related to Media Pillar / Webrtc GW
     * @returns Return an array of Call that are related to Media Pillar / Webrtc GW
     */
    public getActiveMediaPillarAudioCall(): CallRB {
        let mpAudioCall: CallRB;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus === CallStatus.ACTIVE && (call.localMedia & CallMedia.AUDIO) && call.isMediaPillarCall()) {
                    mpAudioCall = call;
                }
            }
        });
        return mpAudioCall;
    }

    //return true if user is in MP call with the same jid
    //THIS IS WRONG ????
    public isInMediaPillarAudioCallWithContactJid(jid) {
        let result = false;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && (call.localMedia & CallMedia.AUDIO) && call.isMediaPillarCall()) {
                    if (call.fullJid === jid) { result = true; }
                }
            }
        });
        return result;
    }

    public hasMediaPillarCallWithContactJid(jid: string): boolean {
        return Object.keys(this.calls).some((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && (call.localMedia & CallMedia.AUDIO) && call.isMediaPillarCall()) {
                    const mpContext: any = call.mediaPillarCall;
                    const telCalls: CallRB[] = mpContext?.telephonyCallRefs || [];
                    return telCalls.some((telCall: CallRB) => {
                        return telCall.contact && telCall.contact.jid === jid;
                    });
                }
            }
            return false;
        });
    }

    public hasOtherWebrtcCallWithContactJid(callId: string, contactJid: string): boolean {
        let result = false;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.id !== callId
                    && call.callStatus !== CallStatus.ENDED
                    && !call.isMediaPillarCall()
                    && call.contact && call.contact.jid === contactJid) {
                    result = true;
                }
            }
        });
        return result;
    }

    /**
     * Return the Call that is on hold (put on hold by the user)
     * @returns Return the Call that is on hold (put on hold by the user)
     */
    public getCallOnHold(): CallRB {
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus === CallStatus.HOLD && !call.isMediaPillarCall()) {
                    result = call;
                }
            }
        });

        return result;
    }

    /**
     * Return the Call that is on hold (put on hold by the distant party)
     * @returns Return the Call that is put on hold
     */
    public getCallPutOnHold(): CallRB {
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.callStatus === CallStatus.PUT_ON_HOLD && !call.isMediaPillarCall()) {
                    result = call;
                }
            }
        });
        return result;
    }

    /**
     * getWebrtcAudioCall
     * @returns the audio webrtc call
     */
    public getWebrtcAudioCall(): CallRB {
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && (call.localMedia & CallMedia.AUDIO) && !call.isMediaPillarCall()) {
                    result = call;
                }
            }
        });
        return result;
    }

    public getWebrtcAudioCalls(): CallRB[] {
        const result: CallRB[] = [];
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && ((call.localMedia & CallMedia.AUDIO) || (call.remoteMedia & CallMedia.AUDIO)) && !call.isMediaPillarCall()) {
                    result.push(call);
                }
            }
        });
        return result;
    }

    public getSecondSharingCallForPbxCall(pbxCall: CallRB): CallRB {
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if ((call.localMedia === CallMedia.SHARING || call.remoteMedia === CallMedia.SHARING) && call.contact && call.contact?.jid === pbxCall.contact?.jid) {
                    //we're in MP call with the same contact, mark this call as webrtc call on top of PBX call
                    result = call;
                }
            }
        });
        return result;
    }

    /**
     * getActiveWebrtcAudioCall
     * @returns the audio webrtc call that is in ACTIVE STATE
     */
    public getActiveWebrtcAudioCall(): CallRB {
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus === CallStatus.ACTIVE && (call.localMedia & CallMedia.AUDIO) && !call.isMediaPillarCall()) {
                    result = call;
                }
            }
        });
        return result;
    }

    /**
     * GetWebrtcCallsOnHold
     * @returns the audio webrtc call that is in HOLD STATE
     */
    public getWebrtcCallsOnHold(): CallRB[] {
        const result: CallRB[] = [];
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus === CallStatus.HOLD && !call.isMediaPillarCall()) {
                    result.push(call);
                }
            }
        });

        return result;
    }

    /*
    * return the "1st" audio webrtc call with the given state not yet tagged as MP but with MP jid
    * mainly for CR25504-24670 & RQ6572
    */
    public getMPWebrtcAudioCallByState(soughtCallState: any): CallRB {
        let result: CallRB = null;
        if (!soughtCallState) { return result; }
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                //CRRAINB-24462 do not use call.isMediaPillarCall() to know if not yet used for MP but check call.mediaPillarCall === null
                if (call.callStatus === soughtCallState && call.mediaPillarCall === null && call.isWebRtcMediaPillar()) {
                    result = call;
                }
            }
        });
        return result;
    }

    /*
    * return the current pure sharing webrtc call
    */
    public getCurrentSharingCall(): CallRB {
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED
                    && (call.localMedia === CallMedia.SHARING || call.remoteMedia === CallMedia.SHARING)) {
                    result = call;
                }
            }
        });
        return result;
    }

    /**
     * Ends all sharing calls related to a given contact
     * @param contact - The contact related to all calls
     */
    public endAllCallsForContact(contact: Contact) {
        if (!contact) {
            return;
        }

        this.logger.info("[webrtcP2PService] endAllCallsForContact " + contact.jid);

        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];

                if (call.callStatus !== CallStatus.ENDED && call.contact && call.contact.jid === contact.jid && !call.isWebRtcMediaPillar() && call.localMedia === CallMedia.SHARING) {
                    this.logger.info("[webrtcP2PService] endAllCallsForContact " + contact.jid + " release call (sharing): " + call);
                    this.releaseCall(call);
                }
            }
        });
    }

    private async onAudioVideoInputDeviceChange() {
        if (this.audioProfileChanging) {
            //ignore
            this.logger.info("[webrtcP2PService] onAudioVideoInputDeviceChange -- ignore");
            return;
        }

        this.logger.info("[webrtcP2PService] onAudioVideoInputDeviceChange");
        const promises = [];
        this.audioProfileChanging = true;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                promises.push(this.updateLocalAudioStreamForCall(call));
            }
        });

        await allSettled(promises);
        this.audioProfileChanging = false;
    }

    public getCallById(callId: string): CallRB {
        this.logger.info("[webrtcP2PService] getCallById " + callId);
        let result: CallRB = null;
        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.id === callId) result = call;
            }
        });
        return result;
    }

    /**
     * Find and return a call by the distant contact's jid or full jid
     * @param contactJid - The jid of the contact that we want to find
     * @param fullJid - [optional] The full JID of the contact related to the call
     * @returns The call
     */
    public getCallByJid(contactJid: string, fullJid: string = ""): CallRB {
        let result: CallRB = null;
        if (!contactJid && !fullJid) return result;
        this.logger.info("[webrtcP2PService] getCallByJid " + contactJid);

        Object.keys(this.calls).forEach((key) => {
            if (this.calls[key]) {
                const call = this.calls[key];
                if (call.contact && call.contact.jid === contactJid) result = call;
            }
        });

        if (!result) {
            this.logger.info("[webrtcP2PService] getCallByJid fallback " + fullJid);
            Object.keys(this.calls).forEach((key) => {
                if (this.calls[key]) {
                    const call = this.calls[key];
                    if (call && call.fullJid === fullJid) result = call;
                }
            });
        }
        return result;
    }

    /**
     * Send to remote (others clients) a dummy reject for an incoming webrtc call
     * then the others remote ressources manage this call as rejected even if locally it isn't
     * (mainly for RQRAINB-9057)
     * @param callId - The tel Call id that we want to remotly manage as rejected
     */
    public dummyRejectCallForRemote(callId: string): void { //RQRAINB-9057
        if (!callId) {
            this.logger.info("[webrtcP2PService] MEDIA | dummyRejectCallForRemote - trying to rejectCall a non existing call !");
            return;
        }
        this.webrtcP2PEventHandler.notifyRemoteOfDummyRejectProposition(callId, "reserved");
    }

    /**
     * Release a standard call
     * @param call - The Call object that we want to release
     * @param reason - (optional) reason to release the call
     * @param text - (optional) additional text
     */
    public releaseCall(call: CallRB, reason: string = "", text: string = ""): void {
        if (!call) {
            this.logger.info("[webrtcP2PService] MEDIA   | releaseCall - trying to release a non existing call !");
            return;
        }

        //clear the makingCall just in case
        this.makingCall = false;

        // Log method call
        this.logger.info("[webrtcP2PService] MEDIA   | releaseCall : " + call);
        try {

            // Release the call
            const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
            this.webrtcConfigAndUtils.disableAllStreamsForSession(session);

            this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");

            // Disable autoReleaseTimeout
            if (this.autoreleaseTimeout) {
                clearTimeout(this.autoreleaseTimeout);
            }

            this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);

            if (this.webrtcConfigAndUtils.callsStats[call.id]) {
                delete this.webrtcConfigAndUtils.callsStats[call.id];
            }

            this.removeCallObject(call, reason, text);

            if (!this.isUserContactInCall()) {
                this.clearSrcObjectsFromElements();
            }
        }
        catch (error) {
            this.logger.info("[webrtcP2PService] MEDIA   | releaseCall ERROR");
            this.logger.error(error);

            this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);

            if (this.webrtcConfigAndUtils.callsStats[call.id]) {
                delete this.webrtcConfigAndUtils.callsStats[call.id];
            }

            this.resetToSafeState(call);

            this.removeCallObject(call);
        }
    }

    public async callShiftPBX(cstaCallId: string, mpJid: string, otherSid: string): Promise<CallRB> {
        this.logger.info(`[WebrtcP2PEventHandler]   | callShift for CSTA call ${cstaCallId} referTo ${mpJid} and other sid ${otherSid}`);
        const mediaToGet = ["audio"];

        const id = "web_" + crypto.randomUUID();

        // Create the call object
        const call = CallRB.create(CallStatus.ENDED, id, CallType.WEBRTC);
        // Don't set the call status to DIALING before setting the fullJid, otherwise this call would trigger an ON_ACTIVE_CALL_CHANGE event in conversationService which will break the current pbx active call in softphone header (call shift Pbx)
        call.fullJid = mpJid;
        call.setStatus(CallStatus.DIALING);

        let stream;
        try {
            stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, undefined, undefined, undefined, call, false, true);

            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
            }

            this.calls[call.id] = call;
            call.isInitiator = true;
            stream.callId = id;

            this.pushInLocalStreams(stream, id);

            this.webrtcP2PEventHandler.setLocalTypeMedia(mediaToGet, call);

            await this.makeJingleCall(call, null, null, otherSid, true);

            // Update presence
            this.contactService.setBusyState("dnd", this.calculatePresenceMessage(call));

            return call;
        }
        catch (error) {
            this.logger.info("[WebrtcP2PEventHandler]   | callShift failure : " + JSON.stringify({ error: error.message }));

            //call is already made
            this.makingCall = false;

            this.removeCallObject(call);

            if (!this.isUserContactInCall()) {
                this.resetToSafeState();
            }

            if (this.autoreleaseTimeout) {
                clearTimeout(this.autoreleaseTimeout);
            }

            if (stream) {
                stream.getTracks().forEach(function (track) {
                    track.enabled = false;
                    track.stop();
                    track = null;
                });

                if (stream.stop) {
                    stream.stop();
                }

                stream = null;
            }

            return null;
        }
    }

    public async callShift(otherSid: string, referTo: string, media: string = "audio") {
        this.logger.info(`[WebrtcP2PEventHandler]   | callShift referTo ${referTo} and other sid ${otherSid}`);
        const mediaToGet = ["audio"];
        if (media === "video") mediaToGet.push("video");

        const contact = await this.contactService.getOrCreateContact(this.xmppService.getBareJidFromJid(referTo));

        const id = "web_" + crypto.randomUUID();

        // Create the call object
        const call = CallRB.create(CallStatus.DIALING, id, CallType.WEBRTC, contact);
        let stream;
        try {
            stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, undefined, undefined, undefined, call);

            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
            }

            this.calls[call.id] = call;
            call.isInitiator = true;
            stream.callId = id;
            call.fullJid = referTo;

            this.pushInLocalStreams(stream, id);

            this.webrtcP2PEventHandler.setLocalTypeMedia(mediaToGet, call);

            this.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT, { callId: id, contact: contact });

            await this.makeJingleCall(call, null, null, otherSid, true);

            // Update presence
            this.contactService.setBusyState("dnd", this.calculatePresenceMessage(call));

            this.eventService.publish("ON_WEBRTC_CALL_TRANSFER_EVENT", contact.jid);
        }
        catch (error) {
            this.logger.info("[WebrtcP2PEventHandler]   | callShift failure : " + JSON.stringify({ error: error.message }));

            //call is already made
            this.makingCall = false;

            this.removeCallObject(call);

            if (!this.isUserContactInCall()) {
                this.resetToSafeState();
            }

            if (this.autoreleaseTimeout) {
                clearTimeout(this.autoreleaseTimeout);
            }

            if (stream) {
                stream.getTracks().forEach(function (track) {
                    track.enabled = false;
                    track.stop();
                    track = null;
                });

                if (stream.stop) {
                    stream.stop();
                }

                stream = null;
            }
        }
    }

    /**
     * Make a Jingle call toward the destionation (could be a MP call)
     * @param call - The Call object
     * @param mediaPillarJid - (optional) the media Pillar Jid only in case of webrtc gateway media Pillar
     * @param phoneNumber - (optional) the number to join by mediaPillar
     * @param otherSid - parameter only used in case of transfer webrtc to webrtc call
     * @param callShift - in case of call shift
     */
    public async makeJingleCall(call: CallRB, mediaPillarJid: string = "", phoneNumber: string = "", otherSid: string = "", callShift: boolean = false) {
        try {
            this.logger.info("[webrtcP2PService] makeJingleCall");

            await this.webrtcConfigAndUtils.setWebrtcConfiguration();

            let localType = "";

            if (call.localMedia & CallMedia.AUDIO) {
                localType = "audio";
            }

            if (call.localMedia & CallMedia.VIDEO) {
                localType = localType ? "audio+video" : "video";
            }
            if (mediaPillarJid && mediaPillarJid !== "") {
                this.logger.info("[webrtcP2PService] MEDIA   | makeJingleCall in mediaPillar case for number : " + phoneNumber);
            }
            // Initiate the call
            if (mediaPillarJid && mediaPillarJid !== "" && phoneNumber && phoneNumber !== "") { //Media pillar case
                localType = "sip";
                await this.xmppService.connection.jingle.initiate(mediaPillarJid, this.xmppService.connection.jid, localType, call.localStreams, call.id, null, phoneNumber);
            }
            else { //Normal case
                await this.xmppService.connection.jingle.initiate(call.fullJid, this.xmppService.connection.jid, localType, call.localStreams, call.id, null, null, false, false, otherSid, callShift);
            }

            // Update the call status
            call.setStatus(CallStatus.CONNECTING);

            // Send event
            this.sendUpdateCallEvent(call);
        }
        catch (error) {
            this.logger.info("[webrtcP2PService] MEDIA   | makeCall failure : " + JSON.stringify({ error: error.message }));

            //call is already made
            this.makingCall = false;

            if (call) {
                this.removeCallObject(call);
            }

            this.resetToSafeState(call);
        }
    }

    /**
     * Make the jingle webtc call with sharing and audio towards the destination in the call
     * @param call - The Call object
     */
    public async makeJingleSharingCall(call: CallRB): Promise<void> {
        try {
            this.logger.info("[webrtcP2PService] MEDIA   | makeJingleSharingCall - make desktop sharing call");

            let type = "sharing";
            if (call.localMedia === CallMedia.SHARING) {
                type = "sharing-only";
            }

            await this.webrtcConfigAndUtils.setWebrtcConfiguration();
            this.logger.info("[webrtcP2PService] SHARING | getUserMedia success");

            // Prepare the callStreams for this sharing call: we must exclude the mediapillar audio calls
            // Let's build an array with localstreams of all mediapillar calls
            const mediaPillarSessionsLocalStreams = [];
            const mediaPillarAudioCalls = this.getMediaPillarAudioCalls();

            mediaPillarAudioCalls.forEach((mediaPillarAudioCall) => {
                const mediaPillarSession = this.xmppService.connection.jingle.sessions[mediaPillarAudioCall.id];
                const mediaPillarStreams = mediaPillarSession ? mediaPillarSession.localStreams : null;
                if (mediaPillarStreams) {
                    // eslint-disable-next-line prefer-spread
                    mediaPillarSessionsLocalStreams.push.apply(mediaPillarSessionsLocalStreams, mediaPillarStreams);
                }
            });

            // We take the callStreams from this.localStreams, but we exclude the mediapillar audio streams
            const callStreamsForSharingCall = (!mediaPillarAudioCalls || mediaPillarAudioCalls.length === 0)
                ? call.localStreams
                : call.localStreams.filter(function (localStream) {
                    return !(mediaPillarSessionsLocalStreams?.includes(localStream));
                });

            // Initiate the call
            await this.xmppService.connection.jingle.initiate(call.fullJid, this.xmppService.connection.jid, type, callStreamsForSharingCall, call.id, null, null);

            // Update the call status
            call.setStatus(CallStatus.CONNECTING);

            // Send event
            this.sendUpdateCallEvent(call);
        }
        catch (error) {
            //call is already made
            this.makingCall = false;

            if (call) {
                this.removeCallObject(call);
            }

            this.resetToSafeState(call);

            this.logger.info("[webrtcP2PService] MEDIA   | makeJingleSharingCall failure : " + JSON.stringify({ error: error.message }));
        }
    }

    /**
     * UCAAS Method -- to be used to start the desktop sharing in case of Desktop App
     * @param contact - The Call object that we want to remove
     * @param media - The media type of call (could be 'sharing' (audio and sharing) and 'sharingOnly' (only sharing, no audio))
     */
    public startDesktopSharing(contact: Contact, media: string) {
        if (this.makingCall) {
            this.logger.info("[webrtcP2PService] | startDesktopSharing already making a call !");
            return;
        }

        //function used by the desktop client in order to define the screen to be taken
        //return TRUE if the action will be taken into account, otherwise FALSE;
        const proceed = !this.askToStartSharing(contact.jid, media, true);

        if (proceed) this.makeCall(contact, media as any);
    }

    /**
     * UCAAS Method -- Desktop app has annulated start of sharing
     */
    public startSharingCancelled() {
        this.makingCall = false;
    }

    public sendUpdateCallEvent(call: CallRB) {
        //update MP webrtc calls via event that is listened in WEBRTC GW service
        if (this.contactService.isMediaPillarJid(call.fullJid)) {
            this.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_MEDIA_PILLAR_CALL_UPDATED_EVENT, { call });
        }
        else {
            //update directly the conversation
            this.conversationService.updateCallInConversation(call);
        }
    }

    /**
     * Answer an incoming jingle call
     * @param call - the call
     */
    public async answerJingleCall(call: CallRB): Promise<void> {
        try {
            this.logger.info("[webrtcP2PService] MEDIA   | answerJingleCall " + call.id);
            // Create the incomming call
            const mediaToGet = [];
            let localType = "audio";

            //audio call
            if (call.localMedia & CallMedia.AUDIO) {
                mediaToGet.push("audio");
            }

            //audio + video call
            if (call.localMedia & CallMedia.VIDEO) {
                mediaToGet.push("video");
                localType = "audio+video";
            }

            //video only call
            if (call.localMedia === CallMedia.VIDEO) {
                localType = "video";
            }

            //if in conversation with mobile, limit the video quality
            let mediaConstraints: any = {};
            if (call.isInConversationWithMobile()) {
                mediaConstraints = this.webrtcConfigAndUtils.getMobileMediaConstraints("video");
            }

            await this.webrtcConfigAndUtils.setWebrtcConfiguration();

            let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, mediaConstraints.fixedVideoWidth, mediaConstraints.fixedVideoHeight, mediaConstraints.fixedFrameRate, null, false, call.isMediaPillarCall());
            const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

            if (!session) {
                //session is already destroyed, cut the streams and close the call
                if (stream) { this.disableStreams([stream]); }
                this.releaseCall(call);
                this.resetToSafeState(call);
                return;
            }

            if (stream) {
                //activate speech enhancement
                if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                    stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
                }
                let modifiedStream = stream;
                if (mediaToGet.indexOf("video") !== -1) {
                    call.originalStream = stream;
                    modifiedStream = this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(stream) : stream;
                    modifiedStream["mediaStreamKind"] = call.originalStream["mediaStreamKind"];
                }
                this.pushInLocalStreams(modifiedStream, call.id);

                session.localStreams.push(modifiedStream);
                await session.updateSessionStreamsAndTransceivers(call.localStreams);

            } else {
                localType = null;
            }

            session.localType = localType;

            session.sendAnswer();
        }
        catch (error) {
            this.logger.info("[webrtcP2PService] MEDIA   | answerJingleCall failure for : " + call + " failure : " + error.message);

            if (error.toString().indexOf("getBrowserMedia") !== -1) {
                this.releaseCall(call);
                //error when trying to get camera stream
                this.openErrorPopup();
            }
            this.resetToSafeState(call);
        }
    }

    /**
     * UCAAS Method -- P2P call is transferred to a SFU conference
     * @param call - The P2P call that should be removed
     */
    public onCallTransferred(call: CallRB) {
        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

        this.webrtcConfigAndUtils.disableAllStreamsForSession(session);
        // Disable autoReleaseTimeout
        if (this.autoreleaseTimeout) {
            clearTimeout(this.autoreleaseTimeout);
        }

        this.webrtcConfigAndUtils.createOrUpdateStatisticsForCall(call);

        if (this.webrtcConfigAndUtils.callsStats[call.id]) {
            delete this.webrtcConfigAndUtils.callsStats[call.id];
        }

        this.removeCallObject(call, "onCallTransferred");
    }

    // ***********************************************************
    // ** ESCALATION ACTION BUTTONS
    // ***********************************************************

    /**
     * UCAAS Method -- Desktop app to add a sharing to conversation
     * @param conversation - The conversation related
     */
    public askToAddDesktopSharing(conversation: ConversationRB) {
        this.logger.info("[webrtcP2PService] MEDIA   | askToAddDesktopSharing - Desktop sharing escalation");

        //function used by the desktop client in order to define the screen to be taken
        //return TRUE if the action will be taken into account, otherwise FALSE;
        const canAddAudio = Boolean(!conversation?.call?.hasRemoteSharingWithAudio);

        const proceed = !this.askToAddSharing(conversation.id, canAddAudio);

        if (proceed) {
            this.addSharingToCall(conversation.videoCall, canAddAudio);
        }
    }

    /**
     * Add audio to call (or change the current audio input device)
     * @param call - The call object
     * @param mediaModify - if True, the action is to "change" the current input audio device,
     *  and not add audio to the current call
     * @returns Resolved when the action is taken into accout
     */
    public async addAudioToCall(call: CallRB, mediaModify: boolean = false): Promise<CallRB> {
        try {
            this.logger.info(`[webrtcP2PService] addAudioToCall ${call.id}`);

            const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

            session.isRenegotiating = true;

            const mediaToGet = ["audio"];

            let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

            //activate speech enhancement
            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
            }

            this.removeAudioVideoStreamFromCall(call);

            this.pushInLocalStreams(stream, call.id);

            const type = mediaModify ? "contentModify" : "contentAdd";

            await this.updateCurrentCall(call, session, "audio", type);

            if (type === "contentModify") {
                session.muteAudio(call.isMuted);
            }
            return call;
        }
        catch (error) {
            this.logger.info(`[webrtcP2PService] addAudioToCall ${call.id} -- error ${stringifyError(error)}`);
            this.releaseCall(call);
            this.resetToSafeState(call);
            return call;
        }
    }

    /**
     * Add Sharing to a existing active call
     * @param call - The call object
     * @returns Resolved when the action is taken into accout
     */
    public async addSharingToCall(call: CallRB, withAudio: boolean = false): Promise<CallRB> {
        this.logger.info(`[webrtcP2PService] addSharingToCall ${call.id}`);
        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        try {

            if (call.hasRemoteSharingWithAudio) withAudio = false;

            session.isRenegotiating = true;
            const mediaToGet = ["sharing"];

            //if in conversation with mobile, limit the video quality
            let mediaConstraints: any = {};
            if (call.isInConversationWithMobile()) {
                mediaConstraints = this.webrtcConfigAndUtils.getMobileMediaConstraints("sharing");
            }

            const stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, null, null, mediaConstraints.fixedFrameRate, null, withAudio);
            this.pushInLocalStreams(stream, call.id);

            await this.updateCurrentCall(call, session, "sharing", "contentAdd");
            this.sendEvent("startHighlighting");

            return call;
        }
        catch (error) {
            this.logger.info(`[webrtcP2PService] addSharingToCall failure ${call.id} -- ${stringifyError(error)}`);
            session.isRenegotiating = false;

            if (error.toString().indexOf("getBrowserMedia") !== -1) {
                //error when trying to get camera stream
                this.openErrorPopup();
            }
            return call;
        }
    }

    /**
     * Add video to active call (or modify the video input)
     * @param call - The call object
     * @param mediaModify - if True, the action is to "change" the current video audio device, and not add video to the current call
     * @returns Resolved when the action is taken into accout
     */
    public async addVideoToCall(call: CallRB, mediaModify: boolean = false): Promise<CallRB> {
        try {
            this.logger.info(`[webrtcP2PService] addVideoToCall for ${call.id}`);

            const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
            const mediaToGet = ["audio", "video"];

            session.isRenegotiating = true;

            //if in conversation with mobile, limit the video quality
            let mediaConstraints: any = {};
            if (call.isInConversationWithMobile()) {
                mediaConstraints = this.webrtcConfigAndUtils.getMobileMediaConstraints("video");
            }

            let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet, mediaConstraints.fixedVideoWidth, mediaConstraints.fixedVideoHeight, mediaConstraints.fixedFrameRate);

            this.removeAudioVideoStreamFromCall(call);

            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
            }

            let modifiedStream = stream;
            if (mediaToGet.indexOf("video") !== -1) {
                call.originalStream = stream;
                modifiedStream = this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(stream) : stream;
                modifiedStream["mediaStreamKind"] = call.originalStream["mediaStreamKind"];
            }

            this.pushInLocalStreams(modifiedStream, call.id);

            const type = mediaModify ? "contentModify" : "contentAdd";

            await this.updateCurrentCall(call, session, "video", type);
            session.muteAudio(call.isMuted);

            return call;
        }
        catch (error) {
            this.logger.info(`[webrtcP2PService] addVideoToCall failure ${call.id} -- ${stringifyError(error)}`);

            if (error.toString().indexOf("getBrowserMedia") !== -1) {
                //error when trying to get camera stream
                this.logger.warn("[webrtcP2PService] addVideoToCall impossible, no webcam plugged");
                this.openErrorPopup();
            }
            else {
                //Another kind of error will lead to call end
                this.releaseCall(call);
                this.resetToSafeState(call);
            }
            return call;
        }
    }

    // change background image for call
    private async changeBackgroundForCall(call: CallRB, data: any, shouldRemoveOld: boolean) {

        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

        session.isRenegotiating = true;

        //remove original stream from call and session object before stopping video streams
        let index = -1;
        call.localStreams.forEach((stream: MediaStream, ind) => {
            if (stream.id === call.originalStream.id) {
                index = ind;
            }
        });

        if (index !== -1) {
            call.localStreams.splice(index, 1);
        }

        index = -1;
        session.localStreams.forEach((stream, ind) => {
            if (stream.id === call.originalStream.id) {
                index = ind;
            }
        });

        if (index !== -1) {
            session.localStreams.splice(index, 1);
        }

        if (shouldRemoveOld) {
            for (let i = 0; i < call.localStreams.length; i++) {
                if (call.localStreams[i]["mediaStreamKind"] === MediaStreamKind.AUDIO || call.localStreams[i]["mediaStreamKind"] === MediaStreamKind.AUDIO_VIDEO_BUNDLE) {
                    const streams = call.localStreams.splice(i, 1);
                    let stream = streams[0];
                    stream.getTracks().forEach((track: MediaStreamTrack) => {
                        track.enabled = false;
                        track.stop();
                        track = null;
                    });
                    stream = null;
                }
            }
        }

        data.stream["mediaStreamKind"] = MediaStreamKind.AUDIO_VIDEO_BUNDLE;

        this.pushInLocalStreams(data.stream, call.id);

        await this.updateCurrentCall(call, session, "video", "contentModify");
        session.muteAudio(call.isMuted);
        this.sendEvent("localMediaUpdate");
    }

    // **********************************************************
    // ** REMOVING MEDIAS FUNCTIONS                            **
    // **********************************************************

    /**
     * Remove the sharing from the call
     * @param call - The call object
     */
    public async removeSharingFromCall(call: CallRB): Promise<CallRB> {
        this.logger.info(`[webrtcP2PService] removeSharingFromCall ${call.id}`);

        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        this.removeSharingStreamFromCall(call);
        session.isRenegotiating = true;
        await this.updateCurrentCall(call, session, "sharing", "contentRemove");

        this.sendEvent("stopHighlighting");
        return call;
    }

    /**
     * Remove the sharing and video from the call
     * @param call - The call object
     * @returns Resolves when the action is taken into account
     */
    private async removeVideoAndSharingFromCall(call: CallRB): Promise<CallRB> {
        try {
            this.logger.info(`[webrtcP2PService] removeVideoAndSharingFromCall for ${call.id}`);

            const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

            this.removeAllStreamsFromCall(call);

            this.sendEvent("stopHighlighting");

            //GET AUDIO
            const mediaToGet = ["audio"];

            session.isRenegotiating = true;

            let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
            }

            this.pushInLocalStreams(stream, call.id);
            call.localMedia = CallMedia.AUDIO;
            await this.updateCurrentCall(call, session, "video", "contentRemove");
            session.muteAudio(true);

            return call;
        }
        catch (error) {
            this.logger.info(`[webrtcP2PService] removeVideoAndSharingFromCall for ${call.id} -- error ${stringifyError(error)}`);

            if (error.toString().indexOf("getBrowserMedia") !== -1) {
                //error when trying to get camera stream
                this.openErrorPopup();
            }
            this.releaseCall(call);
            this.resetToSafeState(call);
            return call;
        }
    }

    /**
     * Remove video from the current call
     * @param call - The call object
     * @returns Resolved when the action is taken into account
     */
    public async removeVideoFromCall(call: CallRB): Promise<CallRB> {
        try {
            this.logger.info(`[webrtcP2PService] removeVideoFromCall for ${call.id}`);

            const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
            this.removeAudioVideoStreamFromCall(call);

            //GET AUDIO
            const mediaToGet = ["audio"];
            session.isRenegotiating = true;

            let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);
            if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
            }

            this.pushInLocalStreams(stream, call.id);

            await this.updateCurrentCall(call, session, "video", "contentRemove");

            if (call.callStatus === CallStatus.PUT_ON_HOLD) {
                // call is put on hold from distant, mute audio
                session.muteAudio(true);
            }
            else session.muteAudio(call.isMuted);

            return call;
        }
        catch (error) {
            this.logger.info(`[webrtcP2PService] removeVideoFromCall failure ${call.id} -- ${stringifyError(error)}`);

            if (error.toString().indexOf("getBrowserMedia") !== -1) {
                //error when trying to get camera stream
                this.openErrorPopup();
            }
            this.releaseCall(call);
            this.resetToSafeState(call);
            return call;
        }
    }

    /*************************************************************/
    /* MUTE / UNMUTE ACTION BUTTONS                              */
    /*************************************************************/

    /**
     * Allow to mute / unmute the current call UCAAS ONLY FONCTION
     * @param conversation - The conversation that is related to the call
     * @param state - if true, the audio in the call will be muted
     * @returns The updated call object
     */
    public muteAudio(conversation: ConversationRB, state: boolean): CallRB {
        const call = conversation.videoCall;
        if (!call) {
            throw (new Error("[webrtcP2PService] MEDIA   | muteAudio - trying to mute a non existing call !"));
        }

        // Log method call
        this.logger.info("[webrtcP2PService] MEDIA   | mute audio: " + call + " | state: " + state);

        conversation.isMutedAudio = state;
        call.isMuted = state;

        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];

        if (session) session.muteAudio(state);
        else throw (new Error("[webrtcP2PService] MEDIA   | muteAudio - trying to mute a non existing session !"));

        this.eventService.publish("ON_CONVERSATION_CALL_MUTED_UPDATED_EVENT", {
            conversation: conversation,
            call: call
        });

        this.conversationService.updateCallCapabilities(call);

        return call;
    }

    /**
     * Put the call on Hold
     * NB this will stop all video and sharing coming from this user before the hold action
     * @param call - The call to be put on hold
     * @returns the updated call object
     */
    private putCallOnHold(call: CallRB): CallRB {
        this.logger.info("[webrtcP2PService] putCallOnHold for call " + call.id);
        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        //save last state of video before putting the call on hold
        if (call.localMedia & CallMedia.VIDEO) {
            call.addVideoAfterUnhold = true;
        }

        if (call.localMedia & CallMedia.VIDEO || call.localMedia & CallMedia.SHARING) {
            this.removeVideoAndSharingFromCall(call);
        }

        if (session) {
            session.sendHold(true);
            session.muteAudio(true);
            call.onHold = true;
            call.setStatus(CallStatus.HOLD);
            this.sendUpdateCallEvent(call);
        }

        this.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);

        return call;
    }

    /**
     * Retrieve the call on Hold
     * @param call - The call to be put on hold
     * @returns the updated call object
     */
    private retrieveCallOnHold(call: CallRB): CallRB {
        this.logger.info("[webrtcP2PService] retrieveCallOnHold for call " + call.id);
        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        if (session) {
            //put any telephony call on hold
            this.eventService.publish("PUT_ACTIVE_TELEPHONY_CALL_ON_HOLD");

            //put on hold any other active webrtc call
            const activeCall = this.getActiveWebrtcAudioCall();
            if (activeCall) {
                this.putCallOnHold(activeCall);
                this.eventService.publish("CREATE_RAINBOW_ANALYTICS_EVENT", { category: "multi-call-management", action: "alternate" });
            }
            else {
                this.eventService.publish("CREATE_RAINBOW_ANALYTICS_EVENT", { category: "multi-call-management", action: "retrieve" });
            }

            session.sendHold(false);
            session.muteAudio(call.isMuted);
            call.onHold = false;
            call.setStatus(CallStatus.ACTIVE);
            this.sendUpdateCallEvent(call);
            this.attachDistantMediaStreams(true, call);
            if (call.addVideoAfterUnhold) {
                this.addVideoToCall(call);
            }
            call.addVideoAfterUnhold = false;
        }

        this.sendEvent(WebrtcP2PServiceEvents.RAINBOW_ON_CALL_UPDATED_EVENT);
        return call;
    }

    /**
     * Functions related to the refer action - update from P2P call to Conference call
     * @returns the updated call object
     */
    public referTo(session, confEndpointId: string, referTo: string): void {
        if (session) session.sendRefer(confEndpointId, referTo);
        else {
            this.logger.info("[webrtcP2PService] MEDIA   | referTo - trying to transfer a non existing session !");
            return;
        }
    }

    /**
     * Transfer a webrtc call on hold to another active call (only works for 2 webrtc P2P calls)
     * @param session - The session related to the call on hold
     * @param otherSession - The active call session, to whom we want to transfer the call
     */
    public transferCall(session: JingleSession, otherSession: JingleSession) {
        if (session && otherSession) session.sendP2PTransferCallRefer(otherSession.sid, otherSession.peerjid);
        else {
            this.logger.info("[webrtcP2PService] MEDIA   | trasnferCall - trying to transfer a non existing session !");
            return;
        }
    }

    public sendReferStatus(callId: string, status: string) {
        const session: JingleSession = this.xmppService.connection.jingle.sessions[callId];
        if (session) {
            this.logger.info("[webrtcP2PService] MEDIA   | sendReferStatus - " + status);
            session.sendReferStatus(status);
        } else {
            this.logger.info("[webrtcP2PService] MEDIA   | sendReferStatus - trying to transfer a non existing session !");
        }
    }

    public sendReferTransfer(callId: string, method: string, referTo: string) { //RQRAINB-5819
        const session: JingleSession = this.xmppService.connection.jingle.sessions[callId];
        if (session) {
            this.logger.info("[webrtcP2PService] MEDIA   | sendReferTransfer - method " + method + " referTo " + referTo);
            session.sendReferTransfer(method, referTo);
        } else {
            this.logger.info("[webrtcP2PService] MEDIA   | sendReferStatus - trying to transfer a non existing session !");
        }
    }

    /* *********************************************************** */
    /* STOP AUDIO/VIDEO Streams                                    */
    /* *********************************************************** */

    /**
     * Stops and removes all Tracks from the Media Stream array
     * @param streams - Array of Media Streams
     */
    public disableStreams(streams: MediaStream[]) {
        if (streams?.length) {
            streams.forEach(function (stream) {
                stream.getTracks().forEach(function (track) {
                    track.enabled = false;
                    track.stop();
                    track = null;
                });

                stream = null;
            });

            //We remove all the localStreams
            while (streams.length > 0) {
                streams.pop();
            }
        }
    }

    public pushInLocalStreams(stream: MediaStream, callId: string) {
        stream[callId] = callId;
        const call = this.calls[callId];
        if (call) call.localStreams.push(stream);
    }

    public openErrorPopup(msg: string = "") {
        if (!msg) msg = "cameraNotAvailable";
        this.eventService.publish("ON_WEBRTC_CALL_ERROR_EVENT", msg);
    }

    private removeSharingStreamFromCall(call: CallRB) {
        //get call related to the session
        if (call?.localStreams.length) {
            //remove sharing stream
            for (let i = 0; i < call.localStreams.length; i++) {
                if (call.localStreams[i]["mediaStreamKind"] === MediaStreamKind.SHARING || call.localStreams[i]["mediaStreamKind"] === MediaStreamKind.SHARING_BUNDLE) {
                    const streams = call.localStreams.splice(i, 1);
                    let stream = streams[0];
                    stream.getTracks().forEach(function (track) {
                        track.enabled = false;
                        track.stop();
                        track = null;
                    });

                    stream = null;
                }
            }
        }
    }

    private removeAudioVideoStreamFromCall(call: CallRB) {
        if (call?.localStreams.length) {
            //remove all audio/video streams for call
            for (let i = 0; i < call.localStreams.length; i++) {
                if (call.localStreams[i]["mediaStreamKind"] === MediaStreamKind.AUDIO || call.localStreams[i]["mediaStreamKind"] === MediaStreamKind.AUDIO_VIDEO_BUNDLE) {
                    const streams = call.localStreams.splice(i, 1);
                    let stream = streams[0];
                    stream.getTracks().forEach((track: MediaStreamTrack) => {
                        track.enabled = false;
                        track.stop();
                        track = null;
                    });
                    stream = null;
                }
            }
        }

        if (call.originalStream) {
            call.originalStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
            call.originalStream = null;
            this.virtualBackgroundService?.stopProcessStream();
        }
    }

    private removeAllStreamsFromCall(call: CallRB) {
        while (call.localStreams.length) {
            let stream = call.localStreams.pop();
            stream.getTracks().forEach((track: MediaStreamTrack) => {
                track.enabled = false;
                track.stop();
                track = null;
            });
            stream = null;
        }

        if (call.originalStream) {
            call.originalStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
            call.originalStream = null;
            this.virtualBackgroundService?.stopProcessStream();
        }
    }

    public resetToSafeState(call: CallRB = null) {
        this.logger.info("[webrtcP2PService] MEDIA   | resetToSafeState");
        // Disable autoReleaseTimeout if present
        if (this.autoreleaseTimeout) {
            clearTimeout(this.autoreleaseTimeout);
        }

        if (call?.localStreams.length) {
            this.disableStreams(call.localStreams);
        }

        if (call?.originalStream) {
            this.disableStreams([call.originalStream]);
        }

        //reset state
        this.eventService.publish("CHECK_AND_RESET_BUSY_STATE");
    }

    public async dropAudio() {
        // drop le track audio
        const stream = await this.webrtcConfigAndUtils.getBrowserMedia(["audio"]);
        const audioTracks = stream.getAudioTracks();
        audioTracks[0].stop();
        audioTracks[0].dispatchEvent(new Event("ended"));
    }

    /*************************************************************/
    /* PRIVATE METHODS                                           */

    /**
     * Method calculatePresenceMessage
     * @private
     * @memberof WebrtcP2PService
     * UCAAS Method -- Calculate the call message to be shown on the UI
     * @param {Call} call call object
     * @returns {string} The new message to be shown for our current presence
     */
    public calculatePresenceMessage(call: CallRB): string {
        let message = "audio";

        if (call && (call.localMedia & CallMedia.VIDEO)) {
            message = "video";
        }

        if (call && (call.remoteMedia & CallMedia.VIDEO)) {
            message = "video";
        }

        if (call && (call.localMedia & CallMedia.SHARING)) {
            message = "sharing";
        }

        return message;
    }

    /**
     * Method updateRemoteTypeMedia
     * @private
     * @memberof WebrtcP2PService
     * UCAAS Method -- Calculate the remoteMedia object value
     * @param {string} remoteType The new remote type string
     * @param {Call} call call object
     */
    public updateRemoteTypeMedia(remoteType: string, call: CallRB) {
        switch (remoteType) {
            case "audio":
                call.remoteMedia = CallMedia.AUDIO;
                break;

            case "video":
                call.remoteMedia = CallMedia.VIDEO;
                break;

            case "audio+video":
                call.remoteMedia = CallMedia.AUDIO | CallMedia.VIDEO;
                break;

            case "sharing":
                call.remoteMedia = CallMedia.SHARING | CallMedia.AUDIO;
                break;

            case "sharing-only":
                call.remoteMedia = CallMedia.SHARING;
                break;

            default:
                break;
        }

        this.logger.debug("[webrtcP2PService] updateRemoteTypeMedia " + call.remoteMedia);
    }

    /* PRIVATE APIs */

    //Internal APIs used for updating the current call (adding video / sharing or removing media)
    private async updateLocalAudioStreamForCall(call: CallRB) {
        const session: JingleSession = this.xmppService.connection.jingle.sessions[call.id];
        if (session) {

            try {
                const mediaToGet = ["audio"];
                const changeVideo = (call.localMedia & CallMedia.VIDEO);
                if (changeVideo) {
                    mediaToGet.push("video");
                }

                //as Firefox is crap, we need to stop all audio streams before getting a new one
                if (changeVideo) {
                    if (call.originalStream) {
                        call.originalStream.getTracks().forEach((track) => { track.enabled = false; track.stop(); track = null; });
                        call.originalStream = null;
                    }
                }

                //find sharing stream and replace it
                let sharingStream;
                if (call.localMedia & CallMedia.SHARING) {
                    //remove sharing stream
                    for (let i = 0; i < session.localStreams.length; i++) {
                        if (session.localStreams[i]?.getVideoTracks().length && !session.localStreams[i].getAudioTracks().length) {
                            sharingStream = session.localStreams.splice(i, 1)[0];
                        }
                    }
                }

                this.webrtcConfigAndUtils.disableMediaStreams(session.localStreams);
                session.localStreams = [];
                call.localStreams = [];

                let stream = await this.webrtcConfigAndUtils.getBrowserMedia(mediaToGet);

                if (this.speechEnhancementService && (this.settingsService.getSetting('speechEnhancementType') !== "none" || this.settingsService.getSetting("enableSpeechEnhancement") === "true") && stream && stream.getAudioTracks().length) {
                    stream = await this.speechEnhancementService.startProcessStream(call.id, stream, "[webrtcP2PService]");
                }

                let modifiedStream = stream;
                if (changeVideo) {
                    call.originalStream = stream;
                    modifiedStream = this.virtualBackgroundService ? await this.virtualBackgroundService.startProcessStream(stream) : stream;
                    modifiedStream["mediaStreamKind"] = call.originalStream["mediaStreamKind"];
                }
                //try to change the audio track "on the fly"
                await session.replaceTransceiverTrack(modifiedStream.getAudioTracks()[0], 0);
                if (changeVideo) {
                    //try to change the audio track "on the fly"
                    await session.replaceTransceiverTrack(modifiedStream.getVideoTracks()[0], 1);
                }

                session.localStreams.push(modifiedStream);
                this.pushInLocalStreams(modifiedStream, call.id);

                if (sharingStream) {
                    session.localStreams.push(sharingStream);
                    this.pushInLocalStreams(sharingStream, call.id);
                }

                session.muteAudio(call.isMuted);

                this.logger.info(`[webrtcP2PService] onAudioVideoInputDeviceChange -- success 'on the fly'`);
                call.sendEvent(CallEvents.ON_CALL_MEDIA_UPDATED);
                this.sendEvent("localMediaUpdate");

                this.conversationService.updateCallCapabilities(call);
            }
            catch {
                // Change the audio track "on the fly" does not work, should renego the session
                if (call.localMedia & CallMedia.VIDEO) this.addVideoToCall(call, true);
                else this.addAudioToCall(call, true);
                this.logger.info(`[webrtcP2PService] onAudioVideoInputDeviceChange -- success 'via transport-replace'`);
            }
        }
        else if (call && !session) {
            //call is probably still in ringing phase, should retry to switch after several seconds
            setTimeout(() => {
                this.onAudioVideoInputDeviceChange();
            }, 2000);
        }
    }

    private updateLocalMediaType(call: CallRB, session: JingleSession): string {
        let result = "audio";

        call.localMedia = 0;

        if (session.localTransceivers[0]?.direction === "sendrecv" || session.localTransceivers[0]?.direction === "sendonly") { call.localMedia = call.localMedia | CallMedia.AUDIO; }
        if (session.localTransceivers[1]?.direction === "sendrecv" || session.localTransceivers[1]?.direction === "sendonly") { call.localMedia = call.localMedia | CallMedia.VIDEO; }
        if (session.localTransceivers[2]?.direction === "sendrecv" || session.localTransceivers[2]?.direction === "sendonly") { call.localMedia = call.localMedia | CallMedia.SHARING; }

        if ((call.localMedia & CallMedia.SHARING) && (call.localMedia & CallMedia.VIDEO)) { result = "sharing+video"; }
        else if ((call.localMedia & CallMedia.SHARING)) { result = "sharing"; }
        else if ((call.localMedia & CallMedia.VIDEO)) { result = "video"; }

        this.logger.info(`[webrtcP2PService] updateLocalMediaType with result ${result} and media ${call.localMedia.toString()}`);
        return result;
    }

    public async updateCurrentCall(call: CallRB, session: JingleSession, type, action): Promise<void> {
        this.logger.info("[webrtcP2PService] updateCurrentCall for type " + type + " and action " + action);

        await session.updateSessionStreamsAndTransceivers(call.localStreams);

        //update the local type
        const newType = this.updateLocalMediaType(call, session);

        switch (action) {
            case "contentAdd": await session.contentAdd(newType); break;
            case "contentRemove": await session.contentRemove(newType); break;
            case "contentModify": await session.contentModify(newType); break;
            default: break;
        }

        // Update presence
        this.contactService.setBusyState("dnd", this.calculatePresenceMessage(call));
        call.sendEvent(CallEvents.ON_CALL_MEDIA_UPDATED);
        this.conversationService.updateCallCapabilities(call);
    }

    private handleToneChangeEvent(event) {
        if (event.tone !== "") {
            this.logger.info("[webrtcP2PService] Dtmf Tone played: " + event.tone);
            this.eventService.publish("ON_DTMF_SENT", event.tone);
        }
    }

    private silentToneChangeEventHandle(event) {
        this.logger.info("[webrtcP2PService] Dtmf Tone played: " + event.tone);
    }

    /*
    * UI FUNCTIONS TO BE REMOVED LATER !!!
    */

    public clearSrcObjectsFromElements() {
        if (Object.keys(this.xmppService.connection.jingle.sessions).length !== 0) {
            this.logger.info("[webrtcP2PService]  clearSrcObjectsFromElements -- ignored because not all calls are terminated");
            return;
        }

        this.RTC.clearMediaStream($("#largevideo"), null);
        this.RTC.clearMediaStream($("#globalAudioTagSharing"), null);
        this.RTC.clearMediaStream($("#globalAudioTag"), null);
    }

    public attachDistantMediaStreamsUnifiedPlan(call) {
        call.hasRemoteSharingWithAudio = false;
        const sess = this.xmppService.connection.jingle.sessions[call.id];
        if (!sess) {
            this.logger.warn("[webrtcP2PService] attachDistantMediaStreamsUnifiedPlan -- missing session " + call.id);
            return;
        }
        // audio
        if (sess.remoteStreamsObject["0"]) {
            this.logger.info("[webrtcP2PService] attachDistantMediaStreamsUnifiedPlan audio track for element globalAudioTag " + sess.remoteStreamsObject["0"].id);
            this.RTC.attachMediaStream($("#globalAudioTag"), sess.remoteStreamsObject["0"]);
        }

        // video & sharing
        if (sess.remoteStreamsObject["1"] && sess.remoteStreamsObject["2"]) {
            const largeVideoElem: any = $("#largevideo");
            this.RTC.attachMediaStreamIfNeeded(largeVideoElem, sess.remoteStreamsObject["2"], sess.remoteStreamsObject["2"].id);
            if (largeVideoElem[0]) {
                largeVideoElem[0].muted = true;
                largeVideoElem[0].volume = 0;
            }
        }
        // video
        else if (sess.remoteStreamsObject["1"]) {
            const largeVideoElem: any = $("#largevideo");
            this.RTC.attachMediaStreamIfNeeded(largeVideoElem, sess.remoteStreamsObject["1"], sess.remoteStreamsObject["1"].id);
            if (largeVideoElem[0]) {
                largeVideoElem[0].muted = true;
                largeVideoElem[0].volume = 0;
            }
        }
        // sharing
        else if (sess.remoteStreamsObject["2"]) {
            const largeVideoElem: any = $("#largevideo");
            this.RTC.attachMediaStreamIfNeeded(largeVideoElem, sess.remoteStreamsObject["2"], sess.remoteStreamsObject["2"].id);
            if (largeVideoElem[0]) {
                largeVideoElem[0].muted = true;
                largeVideoElem[0].volume = 0;
            }

            if (sess.remoteStreamsObject["2"]?.getAudioTracks().length) {
                call.hasRemoteSharingWithAudio = true;
                this.RTC.attachMediaStream($("#globalAudioTagSharing"), sess.remoteStreamsObject["2"]);
            }
        }

        //sharing + audio
        if (sess.remoteStreamsObject["3"]) {
            if (sess.remoteStreamsObject["3"]?.getAudioTracks().length) {
                call.hasRemoteSharingWithAudio = true;
                this.RTC.attachMediaStream($("#globalAudioTagSharing"), sess.remoteStreamsObject["3"]);
            }
        }
    }

    public attachDistantMediaStreams(attach, call) {
        if (attach) {
            if (this.alreadyAttaching) {
                return;
            }
            this.alreadyAttaching = true;
            this.attachDistantMediaStreamsUnifiedPlan(call);
            this.alreadyAttaching = false;
        }
        else {
            if (!this.isUserContactInCall()) {
                this.RTC.clearMediaStream($("#largevideo"), null);
                this.RTC.clearMediaStream($("#globalAudioTagSharing"), null);
                this.RTC.clearMediaStream($("#globalAudioTag"), null);
            }
        }
    }

    //NEW UI FUNCTIONS
    public attachLocalVideoOnlyStreamToElement(call, attach, element) {
        this.logger.info("[webrtcP2PService] attachLocalVideoOnlyStreamToElement " + attach);
        if (attach) {
            if (call.localStreams?.length) {
                call.localStreams.forEach((track) => {
                    //if audio/video track
                    if (track.getVideoTracks().length && track.getAudioTracks().length) {
                        //ignore
                    }
                    //if sharing track
                    else if (track.getVideoTracks().length) {
                        this.RTC.attachMediaStreamIfNeeded(element, track, track.id);
                    }
                });
            }

            //mute local videos -> bug Firefox
            if (element[0]) {
                element[0].volume = 0;
            }
        } else {
            this.RTC.clearMediaStream(element, null);
        }
    }

    public async playStartRecordSoundInCall() {
        try {
            //get the current MP call
            const call = this.getActiveMediaPillarAudioCall();
            this.logger.info(`[webrtcP2PService] playStartRecordSoundInCall for call ${call?.id}`);
            if (call) {
                //replace the current audio stream to play sound and then switch back to audio stream
                let context = new AudioContext();
                let gainNode = context.createGain();
                gainNode.connect(context.destination);
                const audioFileResponse = await fetch('/assets/sounds/beep-01a.ogg');
                const arrayBuffer = await audioFileResponse.arrayBuffer();
                const buffer = await context.decodeAudioData(arrayBuffer);
                const soundSource = context.createBufferSource();
                soundSource.buffer = buffer;
                soundSource.start(0, 0 / 1000);
                soundSource.connect(gainNode);
                const destination = context.createMediaStreamDestination();
                soundSource.connect(destination);

                try {
                    //now we can use the media stream
                    this.logger.info(`[webrtcP2PService] playStartRecordSoundInCall for call ${call?.id} -- replace beep sound`);
                    const session = this.xmppService.connection.jingle.sessions[call.id];
                    await session.replaceTransceiverTrack(destination.stream.getAudioTracks()[0]);
                    //go back after 300ms
                    setTimeout(() => {
                        this.logger.info(`[webrtcP2PService] playStartRecordSoundInCall for call ${call?.id} -- go back to normal audio`);
                        session.replaceTransceiverTrack(session.localStreams[0].getAudioTracks()[0]);

                        context.close();
                        context = undefined;
                        gainNode.disconnect();
                        gainNode = undefined;
                    }, 400);
                }
                catch (error) {
                    this.logger.warn(`[webrtcP2PService] playStartRecordSoundInCall for call ${call?.id} error -- ${stringifyError(error)}`);
                    const session = this.xmppService.connection.jingle.sessions[call.id];
                    session.replaceTransceiverTrack(session.localStreams[0].getAudioTracks()[0]);
                    context?.close();
                    context = undefined;
                    gainNode?.disconnect();
                    gainNode = undefined;
                }
            }
        }
        catch (error) {
            this.logger.info(`[webrtcP2PService] playStartRecordSoundInCall error ${stringifyError(error)}`);
        }
    }
}
